import type {RouteObject as ReactRouterRouteObject} from '../react-router'

/**
 * Converts a React Router-style path string to TanStack Router path syntax.
 *
 * Runtime conversion rules:
 * - `:param?` → `{-$param}` (optional param)
 * - `:param`  → `$param` (required param)
 * - trailing `*` → `$` (splat)
 *
 * This is the runtime half of a pair: it must stay semantically aligned with
 * the type-level converter {@link ConvertToTanStackPath} (and its per-segment
 * helper `ConvertToTanStackSegment`), which model the same segment rewrites for
 * compile-time param inference. Nothing ties the two together automatically, so
 * they MUST be updated together.
 */
export function convertToTanStackPath(path: string): string {
  return path
    .replace(/:([a-zA-Z_][a-zA-Z0-9_]*)\?/g, '{-$$$1}') // optional :param? → {-$param}
    .replace(/:([a-zA-Z_][a-zA-Z0-9_]*)/g, '$$$1') // required :param → $param
    .replace(/\*$/g, '$') // splat * → $
}

/**
 * Type-level mirror of {@link convertToTanStackPath}, so route params can be
 * derived from a path via TanStack's `ResolveParams`.
 *
 * This is a second, hand-maintained implementation of the same rules — nothing
 * ties it to the runtime converter automatically, so the two MUST be updated
 * together. They only agree on the forms current routes use (identifier params,
 * optional params, a trailing splat) and can diverge on shapes no route uses
 * today, e.g. a non-trailing `*` segment (the runtime only rewrites a trailing
 * `*`) or hyphenated param names.
 */
type ConvertToTanStackSegment<S extends string> = S extends `:${infer P}?`
  ? `{-$${P}}`
  : S extends `:${infer P}`
    ? `$${P}`
    : S extends '*'
      ? '$'
      : S

export type ConvertToTanStackPath<P extends string> = P extends `${infer Head}/${infer Rest}`
  ? `${ConvertToTanStackSegment<Head>}/${ConvertToTanStackPath<Rest>}`
  : ConvertToTanStackSegment<P>

/**
 * Converts a React Router `RouteObject` into the path string expected by TanStack Router.
 *
 * - Index routes (`{index: true}`) return `'/'`, which is TanStack Router's relative path
 *   convention for index routes. TSR resolves this relative to the parent's full path.
 * - Pathless layout routes (no `path` property) return `undefined`, causing the caller to assign
 *   an `id` instead.
 * - Parent routes with relative path of `'/'` are normalized as pathless layouts (i.e., path: undefined)
 * - All other routes are forwarded to `normalizeToRelativePath`.
 */
export function routePathAdapter(routeObj: ReactRouterRouteObject, parentFullPath = ''): string | undefined {
  if (routeObj.index === true) {
    return '/'
  }

  if (routeObj.path && routeObj.path !== '') {
    const normalizedPath = normalizeToRelativePath(routeObj.path, parentFullPath)
    // A normalized path of '/' must be treated as a pathless layout if it has children
    // to avoid route identifier conflicts.
    if (normalizedPath !== '/' || !routeObj.children || routeObj.children?.length === 0) {
      return normalizedPath
    }
  }

  // Should resolve as a pathless layout route
  return undefined
}

/**
 * React Router allows both absolute and relative paths. TanStack Router (TSR) expects
 * relative paths for child routes — it resolves them against the parent's full path.
 *
 * For routes with a parent, we always strip the parent prefix so TSR doesn't double-prepend it.
 * For root-level routes (no parent), the path is used as-is with a leading slash.
 */
export function normalizeToRelativePath(reactRouterPath: string, parentFullPath = ''): string {
  const convertedPath = convertToTanStackPath(reactRouterPath)

  const convertedParent = convertToTanStackPath(parentFullPath)

  if (convertedParent) {
    // For absolute paths: strip parent prefix
    if (convertedPath.startsWith('/')) {
      if (convertedPath.startsWith(convertedParent)) {
        const relative = convertedPath.slice(convertedParent.length)
        return relative.startsWith('/') ? relative : `/${relative}`
      }
      // Absolute path doesn't share prefix with parent — shouldn't happen in well-formed trees
      return convertedPath
    }

    // For relative paths: prefix with leading slash (TSR relative paths start with /)
    return convertedPath.startsWith('/') ? convertedPath : `/${convertedPath}`
  }

  // Root-level route (no parent): ensure leading slash
  return convertedPath.startsWith('/') ? convertedPath : `/${convertedPath}`
}
